// Источник данных карточки — единственное место, через которое конструктор ходит наружу.
//
// ЗАЧЕМ ИНТЕРФЕЙС, А НЕ ПРЯМЫЕ ЗАПРОСЫ. Своей схемы (`cir-core/schema_sys.sql`) ещё нет:
// её пишут параллельно. Ждать её значило бы не начинать вовсе, а писать карточку прямо
// на Bitrix значило бы переписать её через месяц, когда Bitrix отключат. Поэтому наружу
// смотрит один интерфейс из шести методов: сегодня за ним поддельные данные (`fake.ts`),
// завтра — BFF, и ни один экран об этом не узнает.
//
// ЧТО ТУТ ВАЖНО ДЛЯ БУДУЩЕГО ТРАНСПОРТА. `сохранить` принимает РЕВИЗИЮ и возвращает
// новую: оптимистическая блокировка в своей схеме уже заложена (`rev` у каждой записи),
// и без неё двое правят карточку одновременно, а побеждает тот, кто нажал позже.
// Расхождение ревизий — это не ошибка сети, и показывать его надо словами.

import type {
  ЗаписьЖурнала,
  ЗаписьКарточки,
  Значения,
  Событие,
  Состояние,
  Сущность,
  Файл,
  Ярлык,
} from './types'

/** Ответ на сохранение. */
export interface ИтогСохранения {
  ревизия: number
  состояние: Состояние
  значения: Значения
  /**
   * id записи. У правки совпадает с тем, что просили; у создания (`id === 'new'`) это
   * ЕДИНСТВЕННОЕ место, где вызывающий узнаёт номер, который выдала база.
   */
  id?: string
}

/** Запись, на которую ссылается наша карточка обратной связью. */
export interface СтрокаСвязи {
  id: string
  значения: Значения
}

export class РевизияРазошлась extends Error {
  /** Ревизия, которая лежит в базе сейчас. Показывается человеку вместе с отказом. */
  readonly вБазе: number

  /** Кто успел раньше — если источник это знает. Слой записи называет имя и время. */
  readonly кто?: string

  constructor(вБазе: number, текст?: string, кто?: string) {
    // Текст источника главнее заготовки: слой записи говорит «Иванов сохранил её минуту
    // назад», и пересказывать это общими словами значило бы выбросить самое полезное.
    super(текст || 'Запись изменил кто-то ещё, пока вы её правили')
    this.name = 'РевизияРазошлась'
    this.вБазе = вБазе
    this.кто = кто
  }
}

/**
 * Слой записи отказал: данные записать нельзя, и он объяснил, что не так с каждым полем.
 *
 * `поля` — {код поля: что не так} РОВНО СЛОВАМИ СЛОЯ ЗАПИСИ, без пересказа: его тексты
 * написаны для человека и называют правило, а не «неверное значение». Форма обязана
 * показать их у полей — списком, за один раз, а не по одному за заход.
 */
export class ОтказЗаписи extends Error {
  readonly поля: Record<string, string>

  constructor(что: string, поля?: Record<string, string>) {
    super(что)
    this.name = 'ОтказЗаписи'
    this.поля = поля ?? {}
  }
}

export interface ИсточникКарточек {
  /** Карточка целиком. null — записи нет (а не «ошибка»: это разные ответы). */
  запись(сущность: Сущность, id: string): Promise<ЗаписьКарточки | null>

  /** Поиск для поля-связи. Пустой запрос — пустой ответ, в ядро не ходим. */
  поиск(сущность: Сущность, запрос: string): Promise<Ярлык[]>

  /** Журнал изменений записи, свежее первым. */
  журнал(сущность: Сущность, id: string): Promise<ЗаписьЖурнала[]>

  /** Файлы записи. */
  документы(сущность: Сущность, id: string): Promise<Файл[]>

  /** Лента событий: письма, звонки, комментарии, смены стадии. */
  лента(сущность: Сущность, id: string): Promise<Событие[]>

  /** Кто ссылается на нашу карточку: сделки компании, акты судна. */
  обратные(сущность: Сущность, id: string, код: string): Promise<СтрокаСвязи[]>

  /**
   * Сохранение. `ревизия` — та, что пришла с записью; разошлась — источник обязан
   * бросить `РевизияРазошлась`, а не молча перезаписать чужую правку.
   */
  сохранить(
    сущность: Сущность,
    id: string,
    значения: Значения,
    ревизия: number,
    состояние?: Состояние,
  ): Promise<ИтогСохранения>
}
